import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Card,
  CardContent,
  Typography,
  Grid,
  Snackbar,
  Alert,
  Box,
} from "@mui/material";
import moment from "moment";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { differenceInDays, addDays } from "date-fns";

import BaseSelect from "components/Shared/Base/BaseSelect";
import { useLazyGetVehicleMileageReportQuery } from "store/services/reportApi";

const VehicleMileageSummary = ({ vehicleId, onDataRefresh = null }) => {
  const [timeRange, setTimeRange] = useState("last7days");
  const [customFromDate, setCustomFromDate] = useState(
    moment().subtract(6, "days").startOf("day").toDate()
  );
  const [customToDate, setCustomToDate] = useState(
    moment().endOf("day").toDate()
  );
  const [errorMessage, setErrorMessage] = useState("");

  // RTK Query - Get vehicle mileage report
  const [
    fetchVehicleMileage,
    { data: vehicleMileageRs, isLoading: loadingMileage },
  ] = useLazyGetVehicleMileageReportQuery();

  // Get date range based on selected time range
  const getDateRange = useCallback((range, from, to) => {
    const today = moment().startOf("day");
    switch (range.toLowerCase()) {
      case "last7days":
        return {
          startDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("YYYY-MM-DD"),
          endDate: today.endOf("day").format("YYYY-MM-DD"),
        };
      case "onemonth":
        return {
          startDate: moment()
            .subtract(1, "month")
            .startOf("day")
            .format("YYYY-MM-DD"),
          endDate: today.endOf("day").format("YYYY-MM-DD"),
        };
      case "custom":
        if (from && to && moment(from).isValid() && moment(to).isValid()) {
          if (moment(to).isBefore(moment(from))) {
            setErrorMessage(
              "Invalid date range. Ensure 'To' date is after 'From' date."
            );
            return { startDate: null, endDate: null };
          }
          if (differenceInDays(new Date(to), new Date(from)) > 90) {
            setErrorMessage("Date range cannot exceed 90 days.");
            return { startDate: null, endDate: null };
          }
          return {
            startDate: moment(from).format("YYYY-MM-DD"),
            endDate: moment(to).format("YYYY-MM-DD"),
          };
        }
        return { startDate: null, endDate: null };
      default:
        return {
          startDate: moment()
            .subtract(6, "days")
            .startOf("day")
            .format("YYYY-MM-DD"),
          endDate: today.endOf("day").format("YYYY-MM-DD"),
        };
    }
  }, []);

  // Fetch mileage data for the specific vehicle
  const fetchMileageData = useCallback(() => {
    if (!vehicleId) return;

    const { startDate, endDate } = getDateRange(
      timeRange,
      customFromDate,
      customToDate
    );

    if (!startDate || !endDate) return;

    const params = {
      vehicleId: vehicleId,
      startDate,
      endDate,
      page: 1,
      limit: 1, // We only need data for this specific vehicle
    };

    fetchVehicleMileage({ params });
  }, [
    vehicleId,
    timeRange,
    customFromDate,
    customToDate,
    fetchVehicleMileage,
    getDateRange,
  ]);

  // Fetch mileage data when dependencies change
  useEffect(() => {
    fetchMileageData();
  }, [fetchMileageData]);

  // Call onDataRefresh when data is updated (optional callback for parent component)
  useEffect(() => {
    if (onDataRefresh && vehicleMileageRs) {
      onDataRefresh(vehicleMileageRs);
    }
  }, [vehicleMileageRs, onDataRefresh]);

  // Get vehicle mileage data from the response
  const vehicleMileageData = React.useMemo(() => {
    if (vehicleMileageRs?.data && vehicleMileageRs.data.length > 0) {
      return vehicleMileageRs.data[0]?.summary || null;
    }
    return null;
  }, [vehicleMileageRs]);

  // Expose refresh function to parent component
  React.useImperativeHandle(
    onDataRefresh,
    () => ({
      refreshMileageData: fetchMileageData,
    }),
    [fetchMileageData]
  );

  if (!vehicleId) {
    return null;
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box mb={3}>
        {/* Time Range Filters */}
        <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mb={2}>
          <BaseSelect
            label="Range"
            value={timeRange}
            onChange={(e) => {
              setTimeRange(e.target.value);
              if (e.target.value !== "custom") {
                setCustomFromDate(null);
                setCustomToDate(null);
                setErrorMessage("");
              } else {
                setCustomFromDate(
                  moment().subtract(6, "days").startOf("day").toDate()
                );
                setCustomToDate(moment().endOf("day").toDate());
              }
            }}
            options={[
              { label: "Last 7 Days", value: "last7days" },
              { label: "One Month", value: "onemonth" },
              { label: "Custom", value: "custom" },
            ]}
            getOptionLabel={(opt) => opt.label}
            getOptionValue={(opt) => opt.value}
            size="small"
            sx={{ minWidth: 140 }}
          />

          {timeRange === "custom" && (
            <>
              <DatePicker
                label="From"
                value={customFromDate}
                onChange={(newValue) => {
                  setCustomFromDate(newValue);
                  setErrorMessage("");
                  if (
                    customToDate &&
                    differenceInDays(customToDate, newValue) > 90
                  ) {
                    setCustomToDate(addDays(newValue, 90));
                    setErrorMessage("Date range adjusted to maximum 90 days.");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 140 }} />
                )}
                maxDate={customToDate || new Date()}
                disableFuture
              />
              <DatePicker
                label="To"
                value={customToDate}
                onChange={(newValue) => {
                  setCustomToDate(newValue);
                  setErrorMessage("");
                  if (
                    customFromDate &&
                    differenceInDays(newValue, customFromDate) > 90
                  ) {
                    setCustomFromDate(addDays(newValue, -90));
                    setErrorMessage("Date range adjusted to maximum 90 days.");
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} size="small" sx={{ width: 140 }} />
                )}
                minDate={customFromDate}
                maxDate={new Date()}
                disableFuture
              />
            </>
          )}
        </Box>

        {/* Mileage Summary Card */}
        {vehicleMileageData && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Vehicle Mileage Summary
                {loadingMileage && (
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                  >
                    {" "}
                    (Loading...)
                  </Typography>
                )}
              </Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Recent Mileage
                  </Typography>
                  <Typography variant="h6" color="success.main">
                    {vehicleMileageData.recentMileage
                      ? `${vehicleMileageData.recentMileage} km/L`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Average Mileage
                  </Typography>
                  <Typography variant="h6">
                    {vehicleMileageData.averageMileage
                      ? `${vehicleMileageData.averageMileage} km/L`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Fuel Quantity
                  </Typography>
                  <Typography variant="h6">
                    {vehicleMileageData.totalFuelQuantityAll
                      ? `${vehicleMileageData.totalFuelQuantityAll} L`
                      : "N/A"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Typography variant="body2" color="text.secondary">
                    Total Fuel Cost
                  </Typography>
                  <Typography variant="h6">
                    Rs. {vehicleMileageData.totalFuelCost?.toFixed(2) || "N/A"}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {/* Error Snackbar */}
        <Snackbar
          open={!!errorMessage}
          autoHideDuration={6000}
          onClose={() => setErrorMessage("")}
        >
          <Alert severity="error" onClose={() => setErrorMessage("")}>
            {errorMessage}
          </Alert>
        </Snackbar>
      </Box>
    </LocalizationProvider>
  );
};

export default VehicleMileageSummary;
